// MY ACCOUNT · Trading access (UI polish, 22 Oct 2026). One read of GET /options-access/me (the same answer the
// server uses to allow or refuse) plus the user's own roles: which segments this account trades, which are switched
// on, in which mode (LIVE = real money; SIMULATED = shadow / paper), and the Zerodha link. Read-only; plain words.
import React, { useEffect, useState } from "react";
import { CheckCircle2, MinusCircle, PlugZap } from "lucide-react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { P } from "@/lib/permissions";
import { BOX_SPREAD_TRADING_ENABLED, IRON_CONDOR_ENABLED, IRON_CONDOR_TRADING_ENABLED, OPTIONS_ENABLED, OPTIONS_HEDGE_ENABLED } from "@/lib/optionsApi";

const PRODUCT_TEXT = { FUTURES_ONLY: "Futures only", OPTIONS_ONLY: "Options only", BOTH: "Futures and Options" };
const MODE_CLS = { LIVE: "bg-[#FF3B30]/15 text-[#FF3B30]", SHADOW: "bg-[#7c3aed]/20 text-[#7c3aed]", PAPER: "bg-[#2563EB]/15 text-[#2563EB]",
  "READ-ONLY": "bg-[var(--term-s3)] text-[var(--term-secondary)]" };
const MODE_TITLE = { LIVE: "Real orders, real money", SHADOW: "Simulated: real orders to an unfunded account, fills simulated on the live book",
  PAPER: "Simulated: filled internally on live market data", "READ-ONLY": "Finds opportunities; places no orders" };

const on = (eff, k) => !!eff?.[k]?.enabled;

/** One row per segment: {key, name, enabled, why, modes: ["LIVE" | "SHADOW" | "PAPER" | "READ-ONLY"]}. Exported for tests. */
export function segmentRows(me, can) {
  const acc = me?.account || null;
  const eff = acc?.effective || {};
  const prod = me?.products || { futures: true, options: false };
  const live = can(P.TRADING_LIVE);
  const noAcc = "Needs a linked Zerodha account enabled by an administrator";
  const rows = [{
    key: "futures", name: "Futures (calendar spreads)", enabled: !!prod.futures,
    why: prod.futures ? null : "This account does not trade Futures",
    modes: [...(acc?.live_enabled && live ? ["LIVE"] : []), ...(acc?.shadow_enabled ? ["SHADOW"] : []), "PAPER"],
  }];
  if (OPTIONS_ENABLED) {
    const optionsOff = !prod.options ? (acc ? "This account does not trade Options" : noAcc) : null;
    rows.push({ key: "options-scanner", name: "Options Scanner", enabled: !!me?.analysis?.allowed,
      why: me?.analysis?.allowed ? null : optionsOff || me?.analysis?.message, modes: ["READ-ONLY"] });
    if (OPTIONS_HEDGE_ENABLED) {
      rows.push({ key: "options-trading", name: "Options trading (BUY / SELL)", enabled: !!me?.hedge?.allowed,
        why: me?.hedge?.allowed ? null : optionsOff || me?.hedge?.message,
        modes: [...(on(eff, "LIVE") && live ? ["LIVE"] : []), ...(on(eff, "SHADOW") ? ["SHADOW"] : [])],
        note: on(eff, "SELL_LIVE") && live ? "SELL OPTIONS may run LIVE on this account" : null });
    }
    if (IRON_CONDOR_ENABLED) {
      rows.push({ key: "box-ic", name: "Box & Iron Condor scanner", enabled: !!me?.iron_condor?.allowed,
        why: me?.iron_condor?.allowed ? null : optionsOff || me?.iron_condor?.message, modes: ["READ-ONLY"] });
      if (BOX_SPREAD_TRADING_ENABLED) rows.push({ key: "box", name: "Box Spread trades", enabled: !!me?.iron_condor?.allowed && on(eff, "BOX_SHADOW"),
        why: on(eff, "BOX_SHADOW") ? (me?.iron_condor?.allowed ? null : optionsOff) : (acc ? "Box Spread simulated trading is not switched on" : noAcc), modes: ["SHADOW"] });
      if (IRON_CONDOR_TRADING_ENABLED) rows.push({ key: "ic", name: "Iron Condor trades", enabled: !!me?.iron_condor?.allowed && on(eff, "IC_SHADOW"),
        why: on(eff, "IC_SHADOW") ? (me?.iron_condor?.allowed ? null : optionsOff) : (acc ? "Iron Condor simulated trading is not switched on" : noAcc), modes: ["SHADOW"] });
    }
  }
  return rows;
}

function ModeChip({ m }) {
  const label = m === "SHADOW" || m === "PAPER" ? `SIMULATED · ${m.toLowerCase()}` : m;
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap ${MODE_CLS[m]}`} title={MODE_TITLE[m]} data-testid={`mode-${m}`}>{label}</span>;
}

export default function AccountAccess({ user, can }) {
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/options-access/me").then((r) => setMe(r.data)).catch((e) => setErr(apiError(e))); }, []);
  if (err) return <div className="text-xs text-[#FF3B30]" data-testid="account-access-error">{err}</div>;
  if (!me) return <div className="text-xs text-[var(--term-muted)]" data-testid="account-access-loading">Loading your trading access…</div>;
  const acc = me.account;
  const rows = segmentRows(me, can);
  const linked = acc && acc.link_status === "ACTIVE";
  const perms = [
    [can(P.TRADING_VIEW), "View markets and trades"],
    [can(P.OPTIONS_TRADE), "Open and manage Options trades"],
    [can(P.TRADING_LIVE), "Trade with real money (LIVE), where the account allows it"],
    [can(P.BROKER), "Connect the broker session"],
    [can(P.USERS_READ), "Administer users"],
  ];
  return (
    <div className="space-y-4" data-testid="account-access">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="rounded-md border border-[var(--term-border)] px-3 py-2" data-testid="account-broker">
          <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)]">Zerodha account</div>
          <div className="text-sm font-bold mono flex items-center gap-1.5">
            {linked ? <CheckCircle2 className="h-4 w-4 text-[#00C805]" /> : <MinusCircle className="h-4 w-4 text-[var(--term-muted)]" />}
            {acc ? acc.broker_user_id || "—" : "Not linked"}</div>
          <div className="text-[11px] text-[var(--term-muted)]">{acc ? (linked ? "Linked" : `Link ${String(acc.link_status || "").toLowerCase()}`) : "Ask an administrator to link one"}</div>
        </div>
        <div className="rounded-md border border-[var(--term-border)] px-3 py-2" data-testid="account-products">
          <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)]">Trades</div>
          <div className="text-sm font-bold">{PRODUCT_TEXT[me.products?.mode] || (me.products?.futures ? "Futures" : "—")}</div>
          <div className="text-[11px] text-[var(--term-muted)]">set by an administrator</div>
        </div>
        <div className="rounded-md border border-[var(--term-border)] px-3 py-2" data-testid="account-live">
          <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)]">Real-money trading</div>
          <div className={`text-sm font-bold ${acc?.live_enabled && can(P.TRADING_LIVE) ? "text-[#FF3B30]" : ""}`}>
            {acc?.live_enabled && can(P.TRADING_LIVE) ? "LIVE allowed" : "Simulated only"}</div>
          <div className="text-[11px] text-[var(--term-muted)]">{can(P.TRADING_LIVE) ? (acc?.live_enabled ? "on this account" : "the account is not enabled for LIVE") : "your role has no LIVE trading"}</div>
        </div>
      </div>

      <div className="overflow-x-auto"><table className="w-full text-xs min-w-[520px]" data-testid="account-segments">
        <thead className="text-[var(--term-muted)] text-[10px] uppercase tracking-wide"><tr>
          <th className="text-left py-1.5">Segment</th><th className="text-left">Status</th><th className="text-left">Mode</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.key} className="border-t border-[var(--term-border)] align-top" data-testid={`segment-${r.key}`}>
            <td className="py-2 pr-3 font-semibold">{r.name}</td>
            <td className="py-2 pr-3">
              {r.enabled ? <span className="text-[#00C805] font-bold inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Enabled</span>
                : <span className="text-[var(--term-muted)] font-bold inline-flex items-center gap-1"><MinusCircle className="h-3.5 w-3.5" />Disabled</span>}
              {r.why && <div className="text-[11px] text-[var(--term-muted)] font-normal">{r.why}</div>}
            </td>
            <td className="py-2"><div className="flex flex-wrap gap-1">{r.enabled && r.modes.length ? r.modes.map((m) => <ModeChip key={m} m={m} />) : <span className="text-[var(--term-muted)]">—</span>}</div>
              {r.enabled && r.note && <div className="text-[11px] text-[#FF3B30] mt-0.5">{r.note}</div>}</td>
          </tr>))}</tbody>
      </table></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)] mb-1">What your role allows</div>
          <ul className="space-y-0.5 text-xs" data-testid="account-permissions">
            {perms.map(([ok, t]) => <li key={t} className={`flex items-center gap-1.5 ${ok ? "" : "text-[var(--term-muted)]"}`}>
              {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[#00C805]" /> : <MinusCircle className="h-3.5 w-3.5" />}{t}</li>)}
          </ul>
          <div className="text-[11px] text-[var(--term-muted)] mt-1">Roles: {(user?.roles || []).join(", ") || "—"}</div>
        </div>
        <div className="text-[11px] text-[var(--term-muted)] space-y-1">
          <p><b className="text-[var(--term-secondary)]">LIVE</b> sends real orders with real money. <b className="text-[var(--term-secondary)]">SIMULATED</b> (shadow or paper) never spends money: fills are simulated on live market data.</p>
          <p>Access is set by an administrator. If something you need is disabled, ask them; the server refuses anything not shown as enabled here.</p>
          {can(P.BROKER) && <Link to="/broker" className="inline-flex items-center gap-1 text-[#2563EB] hover:underline"><PlugZap className="h-3.5 w-3.5" />Broker Session</Link>}
        </div>
      </div>
    </div>
  );
}
